/**
 * Customer Return Plan Store
 *
 * Manages the retailer's plan tier and feature flags for the current return.
 * Plan data is received from the order lookup API response and determines
 * which features are available during the return flow (e.g., FREE vs PREMIUM).
 *
 * @public API:
 * - plan: ref<Object> - Full plan data { planTierName, features }
 * - planTierName: computed<string|null> - The tier name (e.g., 'FREE', 'PREMIUM')
 * - isFreeTier: computed<boolean> - Whether the retailer is on the FREE plan
 * - isPremiumTier: computed<boolean> - Whether the retailer is on the PREMIUM plan
 * - hasPlanFeature: (featureName: string, defaultValue?: boolean) => boolean - Check if a plan feature is enabled
 * - resetPlan: () => void - Reset plan data
 */
export const useCustomerReturnPlanStore = defineStore('customerReturnPlan', () => {
  // ==================== State ====================

  /**
   * Full plan data from the order lookup API
   * @type {import('vue').Ref<{planTierName: string, features: Object} | null>}
   * @public
   */
  const plan = ref(null)

  // ==================== Computed ====================

  /**
   * The plan tier name (e.g., 'FREE', 'PREMIUM')
   * @type {import('vue').ComputedRef<string|null>}
   * @public
   */
  const planTierName = computed(() => plan.value?.planTierName || null)

  /**
   * Whether the retailer is on the FREE plan tier
   * @type {import('vue').ComputedRef<boolean>}
   * @public
   */
  const isFreeTier = computed(() => planTierName.value === 'FREE')

  /**
   * Whether the retailer is on the PREMIUM plan tier
   * @type {import('vue').ComputedRef<boolean>}
   * @public
   */
  const isPremiumTier = computed(() => planTierName.value === 'PREMIUM')

  /**
   * The features map from the plan
   * @type {import('vue').ComputedRef<Object>}
   * @public
   */
  const features = computed(() => plan.value?.features || {})

  // ==================== Actions ====================

  /**
   * Check if a specific feature is enabled in the current plan.
   *
   * @param {string} featureName - Feature name (e.g., 'SHIPPING_LABELS', 'RETURN_WINDOW_ENFORCEMENT')
   * @param {boolean} [defaultValue=true] - Value to return if the feature is absent from the plan.
   *   Use `false` for capabilities: absent = not granted.
   *   Use `true` (default) for restrictions: absent = not enforced (allow by default).
   * @returns {boolean} Whether the feature is enabled. Returns defaultValue if absent.
   * @public
   */
  function hasPlanFeature(featureName, defaultValue = true) {
    const value = features.value[featureName]
    return value === undefined ? defaultValue : value === true
  }

  /**
   * Reset plan data
   * @public
   */
  function resetPlan() {
    plan.value = null
  }

  // ==================== Public API ====================

  return {
    // State
    plan,
    // Computed
    planTierName,
    isFreeTier,
    isPremiumTier,
    features,
    // Actions
    hasPlanFeature,
    resetPlan,
  }
})
